import React from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Crown, Lock, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";

interface UpgradePromptProps {
  featureName: string;
  featureDescription?: string;
  currentPlan?: string;
  requiredPlans?: string[];
}

export const UpgradePrompt: React.FC<UpgradePromptProps> = ({
  featureName,
  featureDescription,
  currentPlan,
  requiredPlans,
}) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleUpgrade = () => {
    navigate("/dashboard/subscription");
  };

  return (
    <div className="flex items-center justify-center min-h-[400px] p-4 flex-1">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Lock className="h-6 w-6 text-primary" />
          </div>
          <CardTitle className="text-xl">
            {t("upgrade.title", { feature: featureName })}
          </CardTitle>
          <CardDescription>
            {featureDescription ||
              t("upgrade.description", { feature: featureName })}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
            <div className="flex items-center space-x-2 flex-col gap-2">
              <span className="text-[12px] text-muted-foreground ">
                {t("upgrade.currentPlan")}
              </span>
              <div className="bg-neutral-200 dark:bg-neutral-800 text-foreground/60 hover:bg-primary/80 rounded-md px-2 py-1 min-w-20 text-center font-bold">
                {currentPlan}
              </div>
            </div>
            <ArrowRight className="h-4 w-4 text-muted-foreground" />
            <div className="flex items-center space-x-2 flex-col gap-2">
              <span className="text-[12px] text-muted-foreground">
                {t("upgrade.requiredPlan")}
              </span>
              <div className="flex items-center space-x-2 gap-2">
                {requiredPlans &&
                  requiredPlans.map((requiredPlan, index) => (
                    <>
                      <div
                        key={requiredPlan}
                        className="bg-amber-400 text-white dark:text-neutral-100 hover:bg-primary/80 rounded-md px-2 py-1 min-w-20 text-center font-bold"
                      >
                        {requiredPlan}
                      </div>
                      {requiredPlans.length > 1 &&
                        index < requiredPlans.length - 1 && (
                          <span className="text-muted-foreground">|</span>
                        )}
                    </>
                  ))}
              </div>
            </div>
          </div>

          <Button onClick={handleUpgrade} className="w-full" size="lg">
            <Crown className="h-4 w-4 mr-2" />
            {t("upgrade.upgradeNow")}
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            {t("upgrade.noCommitment")}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};
